// @ts-nocheck

import { colors, sizes } from '@lmc/common/constants'
import { UserRole } from '@lmc/ts-common'
import { Arrow, Button, Heading, Icon, Modal, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { postJSON } from 'common/services/api'
import { ELIGIBLE_SECOND_SIGNERS_QUERY_KEY } from 'hooks/useEligibleSecondSigners'
import PropTypes from 'prop-types'
import { PureComponent, Suspense } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import DeactivatingTeamMemberModal from 'screens/Rostering/ChangeManagement/DeactivatingTeamMemberModal'
import DeletingTeamMemberModal from 'screens/Rostering/ChangeManagement/DeletingTeamMemberModal'
import styled from 'styled-components'
import { isGroupUser as checkIsGroupUser } from 'utils'
import { handleErrorResponse } from 'utils/error'
import { getSSOManageAccountLink } from 'utils/sso'
import Actions from '../../../../actions'
import ConfirmationDialog from '../../../../LEGACY/ConfirmationDialog'
import Operations from '../../../../operations'
import { history } from '../../../../store'
import Field from '../../../fields'
import {
  AvatarUpload,
  ErrorBox,
  FormFieldLayout,
  ProfilePicture
} from '../../../generic'
import Form from '../..'
import SiteRemovalWarningModal from '../TeamMemberEditForm/SiteRemovalWarningModal'
import {
  resolveSiteRemoval,
  SiteRemovalKind
} from '../TeamMemberEditForm/siteRemoval'
import {
  canViewerEditAccessAndPermissionsOnTarget,
  canViewerEditSitesOnTarget
} from '../TeamMemberEditForm/utils'
import AccountDetailsCard from './cards/AccountDetailsCard'
import ClinicalSystemsAccessCard from './cards/ClinicalSystemsAccessCard'
import CustomIdCard from './cards/CustomIdCard'
import EmergencyContactCard from './cards/EmergencyContactCard'
import EmploymentCard from './cards/EmploymentCard'
import PersonalDetailsCard from './cards/PersonalDetailsCard'
import ProfileEditSafeguardingModal from './ProfileEditSafeguardingModal'
import { safeguardingModalStateFromError } from './safeguardingModalState'

export class TeamMemberEditFormV2 extends PureComponent {
  state = {
    deleteDialogOpen: false,
    deletePrivacyOfficerDialogOpen: false,
    updatePrivacyOfficerDialogOpen: false,
    updatePrivacyOfficerDialogErrorMessage: undefined,
    imageUploadOpen: false,
    resetPasswordLoading: false,
    safeguardingModalOpen: false,
    safeguardingSiteNames: [],
    deactivatingTeamMemberModalOpen: false,
    deletingTeamMemberModalOpen: false,
    siteRemoval: null,
    siteRemovalConfirmed: false,
    siteRemovalDeactivated: false
  }

  formRef = null

  setFormRef = (node) => (this.formRef = node)

  handleCancel = () => {
    const { fields, remove } = this.props
    const { id } = fields
    remove({
      onConfirm: () => history.push(`/organisation/team/${id}/profile`)
    })
  }

  handleComplete = (carer) => {
    const { loadLists, showToast } = this.props
    showToast({ title: 'Updated a Team Member' })
    // Only reload carers so the profile view reflects the edit (CM-3874).
    // We used to also reload 'logs' here, but nothing on the profile/edit
    // screens reads state.data.logs, AND /api/list/logs 403s for group users
    // (they have no `home`). Since loadLists batches with Promise.all, that 403
    // rejected the whole batch and carers never refreshed — so job title / sites
    // stayed stale in the group dashboard until a manual page refresh.
    loadLists(['carers'])
    this.props.queryClient.invalidateQueries(['carers'])
    this.props.queryClient.invalidateQueries(['incident-list'])
    // Keep the second-signature signer list in sync with permission changes.
    this.props.queryClient.invalidateQueries(ELIGIBLE_SECOND_SIGNERS_QUERY_KEY)
    history.push(`/organisation/team/${carer.id}/profile`)
  }

  handleDelete = async () => {
    const {
      deleteSelf,
      fields,
      showToast,
      hasRota,
      flagsRosteringChangeManagementEnabled
    } = this.props
    const { deletingTeamMemberModalOpen, deleteDialogOpen } = this.state

    if (hasRota && flagsRosteringChangeManagementEnabled) {
      if (!deletingTeamMemberModalOpen) {
        this.setState({ deletingTeamMemberModalOpen: true })
        return
      }
    } else if (!deleteDialogOpen) {
      this.setState({ deleteDialogOpen: true })
      return
    }

    const res = await deleteSelf({
      id: fields.id,
      onComplete: () => {
        showToast({ type: 'info', title: 'Deleted a Team Member' })
      }
    })
    if (res.status === 200) {
      // As in handleComplete: the team list is a React Query cache, so without this
      // the deleted member stays listed until a full reload (CM-4794).
      this.props.queryClient.invalidateQueries(['carers'])
      history.push(`/organisation/team`)
      return
    }
    this.setState({ deletePrivacyOfficerDialogOpen: true })

    if (hasRota && flagsRosteringChangeManagementEnabled) {
      this.setState({ deletingTeamMemberModalOpen: false })
    } else {
      this.setState({ deleteDialogOpen: false })
    }
  }

  handleImageSave = (file, imageUri) => {
    this.props.addFormFile({ key: 'picture', file, uri: imageUri })
  }

  currentSiteRemoval = () => {
    const { fields, initial, homes } = this.props
    return resolveSiteRemoval({
      currentHomeId: initial?.home,
      initialHomes: initial?.homes,
      nextHomes: fields?.homes,
      allHomes: homes
    })
  }

  handleSiteRemovalConfirm = () => {
    const { setFormField, initial } = this.props
    const { siteRemoval } = this.state
    const isDeactivation = siteRemoval?.kind === SiteRemovalKind.DEACTIVATE

    // AC4: the backend rejects an empty site list, and an inactive member with no
    // site could not be reactivated without re-assigning one — so put the site
    // back and deactivate instead, which is what the modal copy promises.
    if (isDeactivation) {
      setFormField({ key: 'homes', value: initial?.homes })
      setFormField({ key: 'active', value: false })
    }

    this.setState(
      {
        siteRemoval: null,
        siteRemovalConfirmed: true,
        siteRemovalDeactivated: isDeactivation
      },
      this.handleSubmit
    )
  }

  // Cancelling backs out of the whole save but leaves the form editable, so a
  // site-removal confirmation must not survive it — the admin may now change the
  // Sites tickboxes, and the next save has to re-check them.
  handleDeactivatingTeamMemberModalCancel = () => {
    this.setState({
      deactivatingTeamMemberModalOpen: false,
      siteRemovalConfirmed: false,
      siteRemovalDeactivated: false
    })
  }

  handleSubmit = () => {
    const {
      fields,
      updateItem,
      hasRota,
      flagsRosteringChangeManagementEnabled
    } = this.props
    const {
      deactivatingTeamMemberModalOpen,
      siteRemovalConfirmed,
      siteRemovalDeactivated
    } = this.state

    if (!siteRemovalConfirmed) {
      const siteRemoval = this.currentSiteRemoval()
      if (siteRemoval.kind !== SiteRemovalKind.NONE) {
        this.setState({ siteRemoval })
        return
      }
    }

    // Gated on siteRemovalDeactivated, not siteRemovalConfirmed: a switch
    // confirmation said nothing about deactivation, so an admin who also set
    // Status to inactive in the same save still needs the rota warning.
    if (
      hasRota &&
      flagsRosteringChangeManagementEnabled &&
      !fields.active &&
      !deactivatingTeamMemberModalOpen &&
      !siteRemovalDeactivated
    ) {
      this.setState({ deactivatingTeamMemberModalOpen: true })
      return
    }

    updateItem({
      url: `list/carers/${fields.id}`,
      onComplete: this.handleComplete,
      onError: (e) => {
        // The save was rejected, so a retry must warn again.
        this.setState({
          siteRemovalConfirmed: false,
          siteRemovalDeactivated: false
        })

        if (this.formRef) {
          this.formRef?.scrollIntoView({ behavior: 'smooth' })
        }

        const safeguardingModalState = safeguardingModalStateFromError(e)
        if (safeguardingModalState) {
          this.setState({
            safeguardingModalOpen: true,
            safeguardingSiteNames: safeguardingModalState.siteNames
          })
          return
        }

        const privacyOfficerError = handleErrorResponse(e)

        const isPrivacyOfficerError = privacyOfficerError?.startsWith(
          'This user is a Privacy Officer in'
        )

        if (isPrivacyOfficerError) {
          this.setState({ updatePrivacyOfficerDialogOpen: true })
          this.setState({
            updatePrivacyOfficerDialogErrorMessage: privacyOfficerError
          })
        }
      }
    })

    if (hasRota && flagsRosteringChangeManagementEnabled && !fields.active) {
      this.setState({ deactivatingTeamMemberModalOpen: false })
    }
  }

  clearPin = () => this.props.clearFormFields(['pin', 'pin_confirm'])

  renderPinField = () => {
    const { fields, role } = this.props
    if (role !== UserRole.careHomeAdmin) {
      return null
    }
    // Always start collapsed as a "Change PIN" button; clicking it reveals the
    // New PIN / Confirm PIN inputs + Cancel (PasswordEditField handles toggling).
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Field
          buttonText="Change PIN"
          formId={FORM_ID}
          keys={['pin', 'pin_confirm']}
          label="PIN"
          onCancel={this.clearPin}
          placeholders={['New PIN', 'Confirm PIN']}
          type="changePassword"
          validationTypes="pin"
          values={fields}
        />
      </Suspense>
    )
  }

  renderSummaryField = (fields) => {
    // CM-3864: Notes are read-only for an IT viewer (IT may edit email + status
    // only).
    const accountFieldsOnly = this.props.role === UserRole.it
    return (
      <div style={styles.summaryText}>
        <label htmlFor="form-summary" style={styles.summaryLabel}>
          Notes
        </label>
        <textarea
          value={fields?.summary || ''}
          style={styles.summary}
          id="form-summary"
          className="FormInput"
          disabled={accountFieldsOnly}
          onChange={(e) =>
            this.props.setFormField({
              key: 'summary',
              value: e.target.value
            })
          }
        />
      </div>
    )
  }

  renderSSOResetPassword = () => {
    const { fields, user, showToast } = this.props

    const isCurrentUser = user?.id === fields.id

    const sendResetSSOPassword = () => {
      this.setState({ resetPasswordLoading: true })

      postJSON({
        url: `auth/reset-password/${fields.email}`,
        responseType: 'text'
      })
        .then(() => {
          this.setState({ resetPasswordLoading: false })
          showToast({
            title: 'Reset password email sent. '
          })
        })
        .catch(() => {
          this.setState({ resetPasswordLoading: false })
          showToast({
            title: 'Something went wrong.',
            type: 'error'
          })
        })
    }

    const getResetPasswordField = () => {
      if (isCurrentUser) {
        return (
          <StyledResetHeadingWrapper>
            <a
              href={getSSOManageAccountLink()}
              rel="noreferrer"
              target="_blank"
            >
              <StyledResetHeading level={5}>
                Manage login details for this profile
              </StyledResetHeading>
            </a>
          </StyledResetHeadingWrapper>
        )
      }

      return (
        <Button
          onClick={sendResetSSOPassword}
          isLoading={this.state.resetPasswordLoading}
          disabled={this.state.resetPasswordLoading}
        >
          Send password reset Email
          <Arrow direction="right" size={16} />
        </Button>
      )
    }

    return (
      <FormFieldLayout label="Password" required>
        {getResetPasswordField()}
      </FormFieldLayout>
    )
  }

  renderAvatar = () => {
    const { files, fields } = this.props
    const picture = files?.picture ? files.picture.uri : fields.picture
    return (
      <AvatarRow>
        <ProfilePicture
          edit
          size={64}
          style={{ position: 'relative', overflow: 'hidden' }}
          onClick={() => this.setState({ imageUploadOpen: true })}
          url={picture}
        />
        <EditPictureLink
          onClick={() => this.setState({ imageUploadOpen: true })}
        >
          Edit picture
        </EditPictureLink>
      </AvatarRow>
    )
  }

  renderMainContent = () => {
    const {
      errors,
      fields,
      role,
      initial,
      homes,
      groupAccessManagementEnabled,
      emailIncidentNotificationsFlagEnabled,
      gpConnectEnabled,
      cernerEnabled,
      preferredPeopleNominative,
      managementRoleEnabled,
      auditViewRoleEnabled,
      itSupportRoleEnabled
    } = this.props

    const targetEditable = canViewerEditAccessAndPermissionsOnTarget(
      role,
      initial?.role
    )
    // `accountFieldsOnly` viewers may edit ONLY the target's account fields
    // (email + status); every other field across the cards is read-only. Thread
    // this single capability flag down rather than scattering role checks, so
    // extending it to other roles later is a one-line change here. Currently
    // only the IT & Security role is limited this way (CM-3864).
    const accountFieldsOnly = role === UserRole.it
    const isGroupUser = checkIsGroupUser(initial?.role)
    const homesWithHomeGroup = homes?.filter((home) => !!home?.homeGroup) ?? []

    return (
      <div>
        <ErrorBox
          errors={errors?.map((err) => err?.message || err)}
          style={{ marginBottom: 10 }}
        />
        <AccountDetailsCard
          fields={fields}
          formId={FORM_ID}
          targetEditable={targetEditable}
          sitesEditable={canViewerEditSitesOnTarget(role, initial?.role)}
          accountFieldsOnly={accountFieldsOnly}
          groupAccessManagementEnabled={groupAccessManagementEnabled}
          emailIncidentNotificationsFlagEnabled={
            emailIncidentNotificationsFlagEnabled
          }
          homesWithHomeGroup={homesWithHomeGroup}
          isGroupUser={isGroupUser}
          jobTitle={fields.jobTitle}
          onJobTitleChange={(value) =>
            this.props.setFormField({ key: 'jobTitle', value })
          }
          renderAvatar={this.renderAvatar}
          renderSummaryField={() => this.renderSummaryField(fields)}
          renderPinField={this.renderPinField}
          renderSSOResetPassword={this.renderSSOResetPassword}
          testId={
            this.props.testId
              ? `${this.props.testId}-account-details`
              : undefined
          }
        />
        <PersonalDetailsCard
          fields={fields}
          formId={FORM_ID}
          accountFieldsOnly={accountFieldsOnly}
          testId={
            this.props.testId
              ? `${this.props.testId}-personal-details`
              : undefined
          }
        />
        <ClinicalSystemsAccessCard
          fields={fields}
          formId={FORM_ID}
          gpConnectEnabled={gpConnectEnabled && role === UserRole.careHomeAdmin}
          cernerEnabled={cernerEnabled}
          isGroupUser={isGroupUser}
          accountFieldsOnly={accountFieldsOnly}
          testId={
            this.props.testId
              ? `${this.props.testId}-clinical-systems-access`
              : undefined
          }
        />
        <EmploymentCard
          fields={fields}
          formId={FORM_ID}
          teamMemberEnabled={this.props.teamMemberEnabled}
          accountFieldsOnly={accountFieldsOnly}
          testId={
            this.props.testId ? `${this.props.testId}-employment` : undefined
          }
        />
        <EmergencyContactCard
          fields={fields}
          formId={FORM_ID}
          accountFieldsOnly={accountFieldsOnly}
          testId={
            this.props.testId
              ? `${this.props.testId}-emergency-contact`
              : undefined
          }
        />
        <CustomIdCard
          fields={fields}
          formId={FORM_ID}
          accountFieldsOnly={accountFieldsOnly}
          testId={
            this.props.testId ? `${this.props.testId}-custom-id` : undefined
          }
        />
      </div>
    )
  }

  renderFooter = (isSubmitting) => {
    const { canDeleteUser } = this.props
    return (
      <div className="lmc-fixed-footer" style={styles.footer}>
        {canDeleteUser && (
          <Button
            id="carer-delete"
            onClick={this.handleDelete}
            style={styles.deleteButton}
            variant="text"
            icon={<Icon name="DeleteOutlined" />}
          >
            Delete account
          </Button>
        )}
        <div style={styles.buttonsRow}>
          <Button
            className="carer-edit-cancel"
            onClick={this.handleCancel}
            style={styles.cancelButton}
            variant="default"
          >
            Cancel
          </Button>
          <Button
            className="carer-edit-save"
            loading={isSubmitting}
            onClick={this.handleSubmit}
            variant="primary"
            style={styles.saveButton}
          >
            Save changes
          </Button>
        </div>
      </div>
    )
  }

  render() {
    const { fields, initial, isSubmitting } = this.props
    const {
      deleteDialogOpen,
      imageUploadOpen,
      deletePrivacyOfficerDialogOpen,
      updatePrivacyOfficerDialogOpen,
      safeguardingModalOpen,
      safeguardingSiteNames,
      deactivatingTeamMemberModalOpen,
      deletingTeamMemberModalOpen,
      siteRemoval
    } = this.state

    if (!fields || !initial) {
      return null
    }

    const firstName = fields.name?.first?.trim()
    const lastName = fields.name?.last?.trim()

    const handleSafeguardingModalClose = () => {
      this.props.addForm({ initial, formId: FORM_ID })
      this.setState({ safeguardingModalOpen: false, safeguardingSiteNames: [] })
    }

    return (
      <div
        ref={this.setFormRef}
        style={styles.wrapper}
        data-testid={this.props.testId}
      >
        <div style={styles.mainContainer}>
          <PageTitleCard className="lmc-card">
            <PageTitleText>Editing profile</PageTitleText>
          </PageTitleCard>
          {this.renderMainContent()}
        </div>
        {this.renderFooter(isSubmitting)}
        {deleteDialogOpen && (
          <ConfirmationDialog
            isOpen={deleteDialogOpen}
            onCancel={() => this.setState({ deleteDialogOpen: false })}
            onConfirmation={this.handleDelete}
            confirmationType="danger"
            verificationText={`${firstName} ${lastName}`}
            finalConfirmationText={`Are you sure you want to permanently delete <strong>${firstName} ${lastName}</strong>?`}
            showFinalConfirmationStep={!deletePrivacyOfficerDialogOpen}
          >
            {ConfirmationBody(fields.name)}
          </ConfirmationDialog>
        )}
        <AvatarUpload
          customButton
          isModalOpen={imageUploadOpen}
          onSave={this.handleImageSave}
          onClose={() => this.setState({ imageUploadOpen: false })}
        />

        <Modal
          localisation={{
            title: 'This user is a Privacy Officer',
            confirmText: 'Close'
          }}
          onConfirm={() =>
            this.setState({
              deletePrivacyOfficerDialogOpen: false,
              updatePrivacyOfficerDialogOpen: false,
              deleteDialogOpen: false,
              updatePrivacyOfficerDialogErrorMessage: undefined
            })
          }
          onCancel={() =>
            this.setState({
              deletePrivacyOfficerDialogOpen: false,
              updatePrivacyOfficerDialogOpen: false,
              deleteDialogOpen: false,
              updatePrivacyOfficerDialogErrorMessage: undefined
            })
          }
          isModalVisible={
            deletePrivacyOfficerDialogOpen || updatePrivacyOfficerDialogOpen
          }
          closeButton
          closeOnConfirm={false}
          noPadding
          confirmButtonProps={{ type: 'primary' }}
        >
          <ModalContentWrapper>
            <div>
              <StyledText weight="normal">
                {deletePrivacyOfficerDialogOpen &&
                  `Users who are set as a Home’s Privacy Officer cannot be
                         deleted, set to inactive or have their role changed.`}
                {updatePrivacyOfficerDialogOpen &&
                  this.state.updatePrivacyOfficerDialogErrorMessage}
              </StyledText>
            </div>
            <div style={{ marginTop: 20 }}>
              <StyledText weight="normal">
                {deletePrivacyOfficerDialogOpen &&
                  `To make these changes first remove this user from Privacy
                  Officer list under the Settings `}
                {updatePrivacyOfficerDialogOpen &&
                  `To make these changes first remove this user from Privacy
                  Officer list under other homes`}
                {deletePrivacyOfficerDialogOpen && (
                  <StyledSpan
                    onClick={() => history.push(`/settings/privacy-officers`)}
                  >
                    here
                  </StyledSpan>
                )}
                .
              </StyledText>
            </div>
          </ModalContentWrapper>
        </Modal>

        {deactivatingTeamMemberModalOpen && (
          <DeactivatingTeamMemberModal
            showModal={deactivatingTeamMemberModalOpen}
            onCancel={this.handleDeactivatingTeamMemberModalCancel}
            onConfirm={this.handleSubmit}
            name={`${fields.name?.first} ${fields.name?.last}`}
            carerId={fields.id}
          />
        )}

        {deletingTeamMemberModalOpen && (
          <DeletingTeamMemberModal
            showModal={deletingTeamMemberModalOpen}
            onCancel={() =>
              this.setState({ deletingTeamMemberModalOpen: false })
            }
            onConfirm={this.handleDelete}
            name={`${fields.name?.first} ${fields.name?.last}`}
            carerId={fields.id}
          />
        )}

        {siteRemoval && (
          <SiteRemovalWarningModal
            firstName={fields.name?.first}
            onCancel={() => this.setState({ siteRemoval: null })}
            onConfirm={this.handleSiteRemovalConfirm}
            removal={siteRemoval}
            testId={
              this.props.testId
                ? `${this.props.testId}-site-removal-modal`
                : undefined
            }
          />
        )}

        <ProfileEditSafeguardingModal
          open={safeguardingModalOpen}
          siteNames={safeguardingSiteNames}
          onClose={handleSafeguardingModalClose}
          testId={
            this.props.testId
              ? `${this.props.testId}-safeguarding-modal`
              : undefined
          }
        />
      </div>
    )
  }
}

const ConfirmationBody = (name) => (
  <div>
    Are you sure you want to delete{' '}
    <strong>
      {name.first} {name.last}
    </strong>
    ? If you go ahead, it can't be undone.
    <br />
    <br />
    This team member will no longer be able to log in to the Carer App or the
    Care Office. Their log history will still be visible.
  </div>
)

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const ModalContentWrapper = styled.div`
  border-top: 1px solid;
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.color.neutral.color8};
  padding-top: 24px;
  padding-bottom: 24px;
`

const StyledSpan = styled.span`
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  cursor: pointer;
`

const StyledResetHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
`

const StyledResetHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const PageTitleCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`

const PageTitleText = styled.h1`
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: 0.24px;
  color: #333333;
`

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`

const EditPictureLink = styled.span`
  color: ${({ theme }) => theme.color.status.primary};
  font-weight: 500;
  cursor: pointer;
`

const _isDesktop = isBrowser || isTablet
export const FORM_ID = 'editTeamMember'

const styles = {
  asterisk: {
    color: colors.red,
    fontWeight: 600
  },
  cancelButton: {
    marginRight: 10
  },
  deleteButton: {
    color: theme.color.status.danger,
    padding: '4px 0'
  },
  footer: {
    alignItems: 'center',
    padding: '16px 24px',
    width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw'
  },
  formField: {
    paddingBottom: 20
  },
  image: {
    position: 'relative',
    zIndex: 1,
    marginBottom: 16
  },
  mainContainer: {
    margin: '0 auto',
    paddingBottom: 90,
    maxWidth: sizes.childScreen
  },
  pageTitle: {
    margin: '8px 0 24px'
  },
  requiredText: {
    margin: '0 0 16px'
  },
  wrapper: {
    padding: '24px 20px 0'
  },
  buttonsRow: {
    display: 'flex',
    marginLeft: 'auto'
  },
  saveButton: {
    marginLeft: 4
  },
  summary: {
    minHeight: 130,
    height: '100%',
    minWidth: '100%',
    maxWidth: '100%'
  },
  summaryLabel: {
    fontWeight: 'bold'
  },
  summaryText: {
    minHeight: 150,
    margin: 'auto',
    width: '100%'
  }
}

TeamMemberEditFormV2.propTypes = {
  clearFormFields: PropTypes.func.isRequired,
  deleteSelf: PropTypes.func.isRequired,
  emailIncidentNotificationsFlagEnabled: PropTypes.bool,
  hasRota: PropTypes.bool,
  flagsRosteringChangeManagementEnabled: PropTypes.bool
}

const mapDispatchToProps = (dispatch) => ({
  addForm: ({ initial, formId }) =>
    dispatch(
      Actions.addForm({
        initial,
        formId
      })
    ),
  clearFormFields: (fields) =>
    dispatch(
      Actions.clearFormFields({
        fields,
        formId: FORM_ID
      })
    ),
  deleteSelf: ({ id, onComplete }) =>
    dispatch(
      Operations.deleteFormItem({
        id,
        list: 'carers',
        onComplete
      })
    ),
  loadLists: (lists) => dispatch(Operations.loadLists(lists))
})

const mapStateToProps = (state) => ({
  features: state.global.home,
  role: state.global.user?.role,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  user: state.global.user,
  homes: state.data.homes,
  gpConnectEnabled: !!state.global.home.odsCode && !!state.global.home.asidCode,
  cernerEnabled: state.global.home.features?.cerner?.enabled,
  availablePermissionsTemplates: state.global?.permissionTemplates || [],
  canDeleteUser: state.global.ability
    ? state.global.ability.can('delete', 'User')
    : false,
  managementRoleEnabled: state.global.flags?.management_role?.enabled ?? false,
  auditViewRoleEnabled: state.global.flags?.audit_view_role?.enabled ?? false,
  itSupportRoleEnabled: state.global.flags?.it_role?.enabled ?? false
})

const wrappedForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(TeamMemberEditFormV2)

export default Form(wrappedForm, FORM_ID)
